import * as rd from '@devexperts/remote-data-ts'
import { createSelector, PayloadAction } from '@reduxjs/toolkit'
import { BookingHalfDay, BookingPeriod } from '@spiaggeit/spit-datepicker'
import { DateTime } from 'luxon'

import { convertPeriodToUnixInteger, isSameDay } from '@/utils/dateUtils'
import {
  getBookingPeriodFromHalfDay,
  getHalfDayValueFromQueryString,
} from '@/utils/halfDay'
import { resetAllState, setupByUrl } from './commonActions'
import { RootState } from './configureStore'
import { createAppSlice } from './createAppSlice'
import { licenseSlice } from './licenseSlice'

type Period = { start: string; end: string }

export type PeriodSliceState = {
  period: Period | null
  bookingPeriod: BookingPeriod | null
}

const initialState: PeriodSliceState = {
  bookingPeriod: BookingPeriod.ALL_DAY,
  period: null,
}

export const insertPeriodSlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)

    builder.addCase(setupByUrl, (state, action) => {
      const url = new URL(action.payload)
      const halfDayParamValue = getHalfDayValueFromQueryString(url.searchParams)
      const seasonalParam = url.searchParams.get('seasonal')

      if (seasonalParam === '1') {
        state.bookingPeriod = BookingPeriod.ALL_SEASON
      } else if (halfDayParamValue !== null) {
        state.bookingPeriod = getBookingPeriodFromHalfDay(halfDayParamValue)
      }

      const startDateParam = url.searchParams.get('startdate')
      const endDateParam = url.searchParams.get('enddate')
      const fromParam = url.searchParams.get('from')
      const toParam = url.searchParams.get('to')

      if (startDateParam && endDateParam) {
        const start = DateTime.fromISO(startDateParam)
        const end = DateTime.fromISO(endDateParam)
        const datesParamsAreValid = start.isValid && end.isValid

        if (datesParamsAreValid) {
          state.period = {
            end: endDateParam,
            start: startDateParam,
          }
        }

        //if the url contains a valid start and end date but not half day parameter,
        // we give the bookingPeriod a default value of ALL_DAY
        if (datesParamsAreValid && halfDayParamValue === null) {
          state.bookingPeriod = BookingPeriod.ALL_DAY
        }
      } else if (fromParam && toParam) {
        const start = DateTime.fromSeconds(+fromParam)
        const end = DateTime.fromSeconds(+toParam)
        const datesParamsAreValid = start.isValid && end.isValid

        if (datesParamsAreValid) {
          state.period = {
            end: end.toISODate(),
            start: start.toISODate(),
          }
        }

        if (datesParamsAreValid && halfDayParamValue === null) {
          state.bookingPeriod = BookingPeriod.ALL_DAY
        }
      } else {
        return
      }
    })
  },

  initialState,
  name: 'insertPeriod',
  reducers: (create) => ({
    setBookingPeriod: create.reducer(
      (state, action: PayloadAction<BookingPeriod | null>) => {
        state.bookingPeriod = action.payload
      }
    ),
    setPeriod: create.reducer((state, action: PayloadAction<Period | null>) => {
      state.period = action.payload
    }),
  }),
  selectors: {
    bookingPeriod: (state) => state.bookingPeriod,
    endPeriod: (state) => state.period?.end,
    period: (state) => state.period,
    startPeriod: (state) => state.period?.start,
  },
})
const localeSelector = (_state: RootState, locale: string): string => locale
const timeZoneSelector = (
  _state: RootState,
  _locale: string,
  timeZone: string
): string => timeZone

export const dateStringSelector = createSelector(
  [
    insertPeriodSlice.selectors.startPeriod,
    insertPeriodSlice.selectors.endPeriod,
    localeSelector,
    timeZoneSelector,
  ],
  (start, end, locale, timeZone): string | null => {
    if (!start || !end) return null
    const startDateTime = DateTime.fromISO(start, {
      locale,
      zone: timeZone,
    })
    const endDateTime = DateTime.fromISO(end, {
      locale,
      zone: timeZone,
    })
    const arePeriodsEqual = startDateTime.equals(endDateTime)
    const areMonthsEqual = startDateTime.month === endDateTime.month
    const startDateFormat = arePeriodsEqual || !areMonthsEqual ? 'dd MMM' : 'dd'
    const formattedStart = startDateTime.toFormat(startDateFormat)
    const formattedEnd = arePeriodsEqual ? null : endDateTime.toFormat('dd MMM')
    return formattedEnd ? `${formattedStart} - ${formattedEnd}` : formattedStart
  }
)

export const bookingPeriodSelector = createSelector(
  [
    licenseSlice.selectors.licenseRemoteData,
    licenseSlice.selectors.beachTimeZone,
    insertPeriodSlice.selectors.period,
    insertPeriodSlice.selectors.bookingPeriod,
  ],
  (license, beachTimeZone, period, bookingPeriod): BookingPeriod | null => {
    if (!period || !rd.isSuccess(license)) {
      return null
    }

    if (
      license.value.license.bookingHalfDay === BookingHalfDay.DISABLED ||
      license.value.license.halfDayTimeStart === null
    ) {
      return bookingPeriod
    }

    const today = DateTime.now().startOf('day')
      .setZone('utc')
    const startDate = DateTime.fromISO(period.start).setZone('utc')
    const endDate = DateTime.fromISO(period.end).setZone('utc')

    if (isSameDay(startDate, endDate) && isSameDay(startDate, today)) {
      const limit = DateTime.fromFormat(
        license.value.license.halfDayTimeStart,
        'HH:mm'
      ).setZone(beachTimeZone)

      const now = DateTime.local({ zone: beachTimeZone })

      return now.toSeconds() >= limit.toSeconds()
        ? BookingPeriod.AFTERNOON // afternoon
        : bookingPeriod
    }

    return bookingPeriod
  }
)

export const periodAsUnixTimeSelector = createSelector(
  insertPeriodSlice.selectors.period,
  (period) => convertPeriodToUnixInteger(period)
)

export const dayDifferenceSelector = createSelector(
  insertPeriodSlice.selectors.period,
  (period) => {
    const startDate = DateTime.fromISO(period?.start || '')
    const endDate = DateTime.fromISO(period?.end || '')

    if (!startDate.isValid || !endDate.isValid) {
      return 0
    }
    return endDate.diff(startDate, 'days').days + 1
  }
)
